import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  TextField,
} from "@mui/material";
import { useRef, useState } from "react";
import { useAuth } from "../../hooks/auth/auth";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function ActionButtons(params) {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openReason, setOpenReason] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteInFlight = useRef(false);
  const { state } = useAuth();

  // Opt-in per module: ask for a deletion reason before deleting
  const askDeleteReason = params.askDeleteReason === true;

  const actions = {
    edit:
      params.actions && params.actions.hasOwnProperty("edit")
        ? params.actions.edit
        : true,
    view:
      params.actions && params.actions.hasOwnProperty("view")
        ? params.actions.view
        : true,
    delete:
      params.actions && params.actions.hasOwnProperty("delete")
        ? params.actions.delete
        : true,
  };

  const onEdit = () => {
    params.onEdit(params.id);
  };

  const onView = () => {
    params.onView(params.id);
  };

  const closeReason = () => {
    setOpenReason(false);
    setDeleteReason("");
    setReasonError("");
  };

  const onConfirm = () => {
    if (askDeleteReason) {
      setOpenConfirm(false);
      setDeleteReason("");
      setReasonError("");
      setOpenReason(true);
      return;
    }
    onDelete();
  };

  const onReasonSubmit = () => {
    if (!deleteReason.trim()) {
      setReasonError("Deletion reason is required");
      return;
    }
    onDelete(deleteReason.trim());
  };

  const onDelete = (reason) => {
    // Guard against a double click firing the delete request twice
    if (deleteInFlight.current) return;
    deleteInFlight.current = true;
    setIsDeleting(true);

    params
      .onDelete(params.id, reason)
      .then((res) => {
        deleteInFlight.current = false;
        setIsDeleting(false);
        setOpenConfirm(false);
        closeReason();
        params.refreshHandle();

        if ((res.data && res.data.status === 200) || res.status === 200) {
          const successMessage = res.data.message || "Deletion successful";
          toast.success(successMessage);
        } else if (res.response.data && res.response.status === 200) {
          const successMessage =
            res.response.data.message || "Deletion successful";
          toast.success(successMessage);
        } else {
          toast.error("Deletion failed: Unexpected server response");
        }
      })
      .catch((err) => {
        deleteInFlight.current = false;
        setIsDeleting(false);
        setOpenConfirm(false);
        closeReason();
        params.refreshHandle();

        if (err.response && err.response.data) {
          const errorMessage =
            err.response.data.message ||
            err.response.data.error ||
            "Deletion failed";
          toast.error("Deletion failed: " + errorMessage);
        } else {
          console.error(err);
          toast.error("Deletion failed: Unexpected error response");
        }
      });
  };

  return (
    <>
      {state &&
        (state.userRolepermissions.includes(`${params.module}.delete`) ||
          state.userRole === "Super Admin") &&
        (typeof actions.delete === "function"
          ? actions.delete(params.row)
          : actions.delete) && (
          <Button className="delete" onClick={() => setOpenConfirm(true)}>
            Delete
          </Button>
        )}
      {state &&
        (state.userRolepermissions.includes(`${params.module}.view`) ||
          state.userRole === "Super Admin") &&
        (typeof actions.view === "function"
          ? actions.view(params.row)
          : actions.view) && (
          <Button className="view" onClick={() => onView()}>
            view
          </Button>
        )}
      {state &&
        (state.userRolepermissions.includes(`${params.module}.edit`) ||
          state.userRole === "Super Admin") &&
        (typeof actions.edit === "function"
          ? actions.edit(params.row)
          : actions.edit) && (
          <Button className="edit" onClick={() => onEdit()}>
            Edit
          </Button>
        )}

      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Box
          sx={{ textAlign: "center", padding: "20px" }}
          className="action_btn"
        >
          <DialogTitle id="alert-dialog-title">Confirm</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete the record?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              className="btn save"
              onClick={() => setOpenConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              className="btn cancel"
              onClick={onConfirm}
              disabled={isDeleting}
              autoFocus
            >
              Okay
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {askDeleteReason && (
        <Dialog
          open={openReason}
          onClose={closeReason}
          fullWidth
          maxWidth="sm"
          aria-labelledby="delete-reason-dialog-title"
        >
          <Box sx={{ padding: "20px" }} className="action_btn">
            <DialogTitle
              id="delete-reason-dialog-title"
              sx={{ textAlign: "center" }}
            >
              Reason for Deletion
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Please provide the reason for removing this student from the
                section.
              </DialogContentText>
              <TextField
                fullWidth
                multiline
                rows={4}
                autoFocus
                variant="outlined"
                placeholder="Enter deletion reason..."
                value={deleteReason}
                inputProps={{ maxLength: 500 }}
                error={reasonError !== ""}
                onChange={(e) => {
                  setDeleteReason(e.target.value);
                  if (reasonError) setReasonError("");
                }}
              />
              {reasonError && <FormHelperText error>{reasonError}</FormHelperText>}
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                className="btn save"
                onClick={closeReason}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                className="btn cancel"
                onClick={onReasonSubmit}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
      )}
    </>
  );
}
